import { Base } from '@studiometa/js-toolkit';
import MenuItem from './MenuItem.js';
import { gsap } from 'gsap';
import CustomEase from 'gsap/CustomEase.js';
import CSSPlugin from 'gsap/CSSPlugin.js';

gsap.registerPlugin(CustomEase);
gsap.registerPlugin(CSSPlugin);

/**
 * Toast component
 */
export default class Menu extends Base {
  currentPicture = '';
  imageNumber = 0;
  menuIsOpen = false;
  currentItem = null;

  /**
   * Component Configuration
   */
  static config = {
    name: 'Menu',
    refs: [
      'btn',
      'item[]',
      'pictureContainer',
      'defaultPicture',
      'elements[]',
      'sub',
      'lines[]',
      'itemPicture[]',
    ],
    components: {
      MenuItem,
    },
    emits: ['click-item', 'switch-complete'],
  };

  /**
   * Mount component
   * @returns {void}
   */
  mounted() {
    this.initOpenTimeline();
  }

  /**
   * Create Timeline to animate menu
   */
  initOpenTimeline() {
    this.openTimeline = gsap.timeline();
    this.openTimeline.pause();

    this.openTimeline.set(this.$refs.sub, { display: 'block' });

    this.openTimeline.to(this.$refs.lines, {
      scaleY: 1,
      scaleX: 1,
      duration: 1,
      ease: CustomEase.create('custom', '0.215, 0.61, 0.355, 1'),
    });

    this.openTimeline.fromTo(
      this.$refs.elements,
      {
        xPercent: 10,
        autoAlpha: 0,
      },
      {
        xPercent: 0,
        autoAlpha: 1,
        duration: 1,
        stagger: {
          amount: 0.5,
          ease: CustomEase.create('custom', '0.215, 0.61, 0.355, 1'),
        },
      },
      '<+=0.05',
    );

    if (!this.isMobile) {
      this.openTimeline.fromTo(
        this.$refs.defaultPicture,
        {
          'clip-path': 'inset(100% 0% 0% 0%)',
        },
        {
          'clip-path': 'inset(0% 0% 0% 0%)',
          duration: 1.5,
          ease: 'power3.inOut',
        },
        '<+=0.05',
      );
    }
  }

  /**
   * Test if we are on the mobile version of the component or not.
   * @returns {boolean}
   */
  get isMobile() {
    return ['xxs', 'xs', 's'].includes(this.$services.get('resized').breakpoint);
  }

  /**
   * Click on main menu item
   */
  onClick() {
    this.$emit('click-item');
  }

  /**
   * Simple open menu
   */
  openMenu() {
    this.$refs.btn.classList.add('menu__item--current');
    this.openTimeline.restart();
    this.menuIsOpen = true;
  }

  /**
   * Simplye close menu
   */
  closeMenu() {
    this.$refs.btn.classList.remove('menu__item--current');
    gsap.to(this.$refs.sub, {
      'clip-path': 'inset(100% 0% 0% 0%)',
      ease: 'power3.inOut',
      duration: 0.3,
      onComplete: () => {
        gsap.set(this.$refs.sub, { display: 'none', 'clip-path': 'inset(0% 0% 0% 0%)' });
        this.$parent.closeMenuElements();
      },
    });
    this.menuIsOpen = false;
  }

  /**
   * Launched when menu is already open
   */
  switchMenu() {
    this.menuIsOpen = false;
    this.$refs.btn.classList.remove('menu__item--current');
    gsap.to(this.$refs.sub, {
      opacity: 0,
      ease: 'power3.inOut',
      duration: 0.4,
      onComplete: () => {
        gsap.set(this.$refs.sub, { display: 'none', opacity: '1' });
        this.$emit('switch-complete');
      },
    });
  }

  /**
   * Hover item
   * @param {object} props Props
   * @param {object} props.target current element
   * @returns {void}
   */
  onItemPictureMouseenter({ target }) {
    if (this.currentItem === target) {
      return;
    }

    this.currentItem = target;

    this.currentPicture = target.dataset.picture;
    this.createNewImage();
  }

  /**
   * Create new img element in container
   * @returns {void}
   */
  createNewImage() {
    if (this.imageNumber > 3) {
      this.flushImages();
    }
    const img = document.createElement('img');
    img.setAttribute(
      'src',
      this.currentPicture ? this.currentPicture : this.$refs.defaultPicture.src,
    );
    img.className =
      'absolute top-0 left-0 size-full object-center object-cover z-1 picture-added transition duration-500 z-1';
    img.setAttribute('alt', '');
    img.setAttribute('style', `z-index:${this.imageNumber}`);
    this.$refs.pictureContainer.insertBefore(img, this.$refs.pictureContainer.firstChild);
    this.imageNumber += 1;
  }

  /**
   * Remove old images
   * @returns {void}
   */
  flushImages() {
    const images = this.$refs.pictureContainer.querySelectorAll('img');

    images.forEach((element, index) => {
      if (index !== 0) {
        element.remove();
      }
    });
  }
}
